import {Combobox, Transition} from "@headlessui/react";
import {motion} from "framer-motion";
import {FC, Fragment, useEffect, useState} from "react";
import {FaAngleDown, FaCheck} from "react-icons/fa";
import {CgSpinner} from "react-icons/cg";
import { FixedSizeList } from "react-window";

export type onAutocompleteChangeHandler<T> = (type: OptionsType<T>) => void;

interface Props<TValueType> {
    options: OptionsType<TValueType>[],
    onChange?: (type: OptionsType<TValueType>) => void;
    defaultValue?: OptionsType<TValueType>,
    loading?: boolean;
    placeholder?: string;
}

type Options<T> = {
    value: T
}
type OptionsType<T> = Options<T> & {
    label: string;
}

function Autocomplete<T>(props: Props<T>) {
    const [selected, setSelected] = useState<OptionsType<T>|undefined>(props.defaultValue);
    const [query, setQuery] = useState('');

    const onChange = (value: OptionsType<T>) => {
        if(props.onChange) props.onChange(value);
        setSelected(value);
    }

    useEffect(() => {
        setSelected(props.defaultValue);
    },[props.options, props.defaultValue])

    const filteredPeople =
        query === ''
            ? props.options
            : props.options.filter((option) =>
                option.label
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            )

    return (
        <motion.div initial={{translateX: -50, opacity: 0}} animate={{translateX: 0, opacity: 1}}
                    exit={{translateX: -50, opacity: 0}}>
            <Combobox value={selected} onChange={onChange}>
                <div className='relative mt-1'>
                    <div
                        className="relative bg-transparent focus-visible:ring-opacity-75 shadow dark:border border-cardGray dark:border-slate-600 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full overflow-hidden text-black dark:text-accentGray sm:text-sm text-left cursor-default disabled:cursor-progress placeholder-cardGray">
                        <Combobox.Input
                            className="bg-transparent py-3 pr-10 pl-4 border-none focus:ring-0 w-full text-black dark:text-white text-sm leading-5"
                            displayValue={(item: OptionsType<T>) => item?.label ? item.label : ''}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                        <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
                            {props.loading ? <CgSpinner className='text-xl animate-spin'/> : <FaAngleDown className='text-xl'/>}
                        </Combobox.Button>
                    </div>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <Combobox.Options
                            className="z-10 absolute bg-gray-200 dark:bg-cardGray shadow-lg mt-1 py-1 rounded-lg w-full max-h-60 overflow-auto text-black dark:text-white text-base">
                            {filteredPeople.length === 0 && query !== '' ? (
                                <div className="relative px-4 py-2 pr-4 pl-10 text-gray-700 dark:text-white text-sm cursor-default select-none">
                                    Nothing found.
                                </div>
                            ) : (
                                <FixedSizeList
                                    height={240} // 60 * 4 items
                                    itemCount={filteredPeople.length}
                                    itemSize={48} // px per item
                                    width="100%"
                                >
                                    {({ index, style }) => {
                                        const option = filteredPeople[index];
                                        return (
                                            <div style={style} key={index}>
                                                <Combobox.Option
                                                    className={({active}) => `sm:text-sm cursor-default select-none relative py-2 pl-10 pr-4 border-b border-black/5 dark:border-white/5 last:border-none ${active ? 'text-white bg-primary' : 'text-black dark:text-white'}`}
                                                    value={option}
                                                >
                                                    {({selected, active}) => (
                                                        <div>
                                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{option.label}</span>
                                                            {selected && (
                                                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white bg-primary' : 'text-white'}`}>
                                                                    <FaCheck className="w-5 h-5" aria-hidden="true"/>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </Combobox.Option>
                                            </div>
                                        );
                                    }}
                                </FixedSizeList>
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>
            </Combobox>
        </motion.div>
    );
}

(Autocomplete as FC<Props<any>>).defaultProps = {
    loading: false
}

export default Autocomplete;